import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { translateRoomType } from '../../../constants/roomTypes';

const RoomCard = ({ room, onEdit, onDuplicate, onDelete, onViewDetails }) => {
  const getStatusConfig = (status) => {
    const statusMap = {
      'AVAILABLE': { bg: 'bg-success/90', text: 'text-white', icon: 'CheckCircle', label: 'Disponível' },
      'OCCUPIED': { bg: 'bg-destructive/90', text: 'text-white', icon: 'BedDouble', label: 'Ocupado' },
      'MAINTENANCE': { bg: 'bg-warning/90', text: 'text-white', icon: 'Wrench', label: 'Manutenção' },
      'CLEANING': { bg: 'bg-secondary/90', text: 'text-white', icon: 'Sparkles', label: 'Limpeza' }
    };
    return statusMap[status] || { bg: 'bg-muted', text: 'text-muted-foreground', icon: 'CircleDot', label: status };
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const statusConfig = getStatusConfig(room.status);

  return (
    <div className="group relative bg-gradient-to-br from-card to-card/50 border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl opacity-20 -mr-16 -mt-16 group-hover:opacity-30 transition-opacity"></div>
      
      {/* Room Image */}
      <div className="relative">
        <div className="w-full h-48 bg-gradient-to-br from-muted to-muted/50 overflow-hidden">
          <Image
            src={room.image}
            alt={`Quarto ${room.number}`}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        </div>
        
        {/* Status Badge */}
        <div className={`absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 ${statusConfig.bg} ${statusConfig.text} rounded-lg shadow-lg backdrop-blur-sm`}>
          <Icon name={statusConfig.icon} size={14} />
          <span className="text-xs font-semibold">{statusConfig.label}</span>
        </div>
        
        {/* Room Number Badge */}
        <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 bg-card/90 backdrop-blur-sm rounded-lg shadow-lg border border-border/50">
          <Icon name="Home" size={14} className="text-primary" />
          <span className="text-sm font-bold text-foreground">#{room.number}</span>
        </div>
      </div>

      {/* Room Info */}
      <div className="relative p-5 space-y-4">
        {/* Title and Type */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-foreground mb-1 group-hover:text-primary transition-colors">
              Quarto {room.number}
            </h3>
            <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-lg">
              {translateRoomType(room.type)}
            </span>
          </div>
        </div>

        {/* Capacidade e tipos de cama (ícones) */}
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-primary/10 rounded-lg border border-primary/20"
            title={`Capacidade: ${room.capacity} pessoas`}
          >
            <Icon name="Users" size={16} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">{room.capacity}</span>
          </div>
          {(room.bedsSolteiro > 0 || room.bedsCasal > 0 || room.bedsQueen > 0 || room.bedsKing > 0 || room.bedsBeliche > 0) ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {room.bedsSolteiro > 0 && (
                <div
                  className="flex items-center gap-1 px-2 py-1 bg-muted/50 rounded-lg"
                  title={`${room.bedsSolteiro} Solteiro(s)`}
                >
                  <Icon name="Bed" size={14} className="text-muted-foreground" />
                  <span className="text-xs font-medium text-foreground">{room.bedsSolteiro}</span>
                </div>
              )}
              {room.bedsCasal > 0 && (
                <div
                  className="flex items-center gap-1 px-2 py-1 bg-muted/50 rounded-lg"
                  title={`${room.bedsCasal} Casal`}
                >
                  <Icon name="BedDouble" size={14} className="text-muted-foreground" />
                  <span className="text-xs font-medium text-foreground">{room.bedsCasal}</span>
                </div>
              )}
              {room.bedsQueen > 0 && (
                <div
                  className="flex items-center gap-1 px-2 py-1 bg-muted/50 rounded-lg"
                  title={`${room.bedsQueen} Queen`}
                >
                  <Icon name="BedDouble" size={14} className="text-muted-foreground" />
                  <span className="text-xs font-medium text-foreground">{room.bedsQueen}</span>
                </div>
              )}
              {room.bedsKing > 0 && (
                <div
                  className="flex items-center gap-1 px-2 py-1 bg-muted/50 rounded-lg"
                  title={`${room.bedsKing} King`}
                >
                  <Icon name="BedDouble" size={14} className="text-muted-foreground" />
                  <span className="text-xs font-medium text-foreground">{room.bedsKing}</span>
                </div>
              )}
              {room.bedsBeliche > 0 && (
                <div
                  className="flex items-center gap-1 px-2 py-1 bg-muted/50 rounded-lg"
                  title={`${room.bedsBeliche} Beliche(s)`}
                >
                  <Icon name="Layers" size={14} className="text-muted-foreground" />
                  <span className="text-xs font-medium text-foreground">{room.bedsBeliche}</span>
                </div>
              )}
            </div>
          ) : (
            <div
              className="flex items-center gap-1 px-2 py-1 bg-muted/50 rounded-lg"
              title={`${room.beds || 0} cama(s)`}
            >
              <Icon name="Bed" size={14} className="text-muted-foreground" />
              <span className="text-xs font-medium text-foreground">{room.beds || 0}</span>
            </div>
          )}
        </div>

        {/* Amenities */}
        <div className="flex flex-wrap gap-2">
          {room.hasAC && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-muted/50 to-muted/30 rounded-lg border border-border/50">
              <Icon name="Wind" size={14} className="text-muted-foreground" />
              <span className="text-xs font-medium">AC</span>
            </div>
          )}
          {room.hasMinibar && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-muted/50 to-muted/30 rounded-lg border border-border/50">
              <Icon name="Coffee" size={14} className="text-muted-foreground" />
              <span className="text-xs font-medium">Frigobar</span>
            </div>
          )}
          {room.hasBathroom && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-muted/50 to-muted/30 rounded-lg border border-border/50">
              <Icon name="Bath" size={14} className="text-muted-foreground" />
              <span className="text-xs font-medium">Banheiro</span>
            </div>
          )}
        </div>

        {/* Pricing */}
        <div className="pt-4 border-t border-border space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Solteiro</span>
            <span className="text-sm font-bold text-foreground">{formatPrice(room.singlePrice)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Casal</span>
            <span className="text-lg font-bold text-primary">{formatPrice(room.couplePrice)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2">
          <button
            onClick={() => onViewDetails(room)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-all"
          >
            <Icon name="Eye" size={16} />
            <span className="text-sm">Ver Detalhes</span>
          </button>
          
          <button
            onClick={() => onEdit(room)}
            className="p-2 rounded-lg bg-card hover:bg-accent border border-border transition-all"
            title="Editar"
          >
            <Icon name="Edit" size={18} className="text-foreground" />
          </button>
          
          <button
            onClick={() => onDuplicate(room)}
            className="p-2 rounded-lg bg-card hover:bg-accent border border-border transition-all"
            title="Duplicar"
          >
            <Icon name="Copy" size={18} className="text-foreground" />
          </button>
          
          <button
            onClick={() => onDelete(room)}
            className="p-2 rounded-lg bg-card hover:bg-destructive/10 border border-border hover:border-destructive/30 transition-all"
            title="Excluir"
          >
            <Icon name="Trash2" size={18} className="text-destructive" />
          </button>
        </div>

      </div>
    </div>
  );
};

export default RoomCard;